import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AGCDynamicSelect } from "../../generic-components/AGCDynamicSelect";
import { AGCStaticSelect } from "../../generic-components/AGCStaticSelect";
import { apiGenericUpdate, genericGetOne, genericSelectGetAll } from "../generic/crud";
import { ENTITY_ID_INVENTORY_ITEM } from "../generic/Constants";
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from "../../utils/toast/Toast";

// Indian money format: xx,xx,xxx.00
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
    }).format(amount);
};

// Effective price for an item, honouring per-customer custom pricing (sales only)
const getEffectivePrice = (item, partyId, applyCustomPricing) => {
    if (!applyCustomPricing || !partyId || !item?.customCustomerPricing?.length) return item?.price;
    const match = item.customCustomerPricing.find(cp => cp.customerId === partyId);
    return match ? match.price : item.price;
};

/**
 * Small dialog to change the party (customer / supplier) and the product on a
 * pending two-step entry. Shared by Pending Sales and Pending Purchases.
 */
export function PendingEntryEditDialog({
    show,
    row,
    entityId,
    partyField,
    partyEntityId,
    partyLabel,
    applyCustomPricing = false,
    onClose,
    onSaved,
}) {
    // Options carry a display name with the unit; `item` holds the real product
    const [productOptions, setProductOptions] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const formik = useFormik({
        initialValues: {
            party: row?.[partyField] || null,
            product: null,
        },
        validationSchema: Yup.object({
            party: Yup.object().nullable().required(`${partyLabel} is required`),
            product: Yup.object().nullable().required("Product is required"),
        }),
        enableReinitialize: true,
        onSubmit: (values) => saveChanges(values),
    });

    // Load inventory products when the dialog opens, then preselect the current one
    useEffect(() => {
        if (!show) return;
        setLoadingItems(true);
        genericSelectGetAll(ENTITY_ID_INVENTORY_ITEM)
            .then(({ data }) => {
                const options = (data || [])
                    .filter(item => item.inventoryType === 'INVENTORY')
                    .map(item => {
                        const product = {
                            id: item.value || item.id,
                            name: item.label || item.name || item.itemName,
                            price: item.price,
                            unitSymbol: item.unitSymbol,
                            taxPlans: item.taxPlan || [],
                            hsnCode: item.hsnCode,
                            customCustomerPricing: item.customCustomerPricing || [],
                        };
                        return {
                            id: product.id,
                            name: product.unitSymbol ? `${product.name} (${product.unitSymbol})` : product.name,
                            item: product,
                        };
                    });
                setProductOptions(options);
                const currentId = row?.itemsList?.[0]?.id;
                formik.setFieldValue('product', options.find(o => o.id === currentId) || null);
            })
            .catch(() => setProductOptions([]))
            .finally(() => setLoadingItems(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, row]);

    const partyId = formik.values.party?.id || formik.values.party?.value;
    const selectedProduct = formik.values.product?.item;

    const saveChanges = useCallback(async (values) => {
        const product = values.product?.item;
        if (!product) {
            showToast("Please select a Product", TOAST_FAILURE);
            return;
        }
        setIsSaving(true);
        try {
            // Re-read the record so untouched fields (weights, photos, payments) are preserved
            const { data: entry } = await genericGetOne(entityId, row.id);
            const selectedPartyId = values.party?.id || values.party?.value;
            const existingItems = entry.itemsList || [];
            const firstItem = existingItems[0] || { quantity: 0, discountAmount: 0 };

            const updated = {
                ...entry,
                [partyField]: {
                    id: selectedPartyId,
                    name: values.party?.name || values.party?.label,
                },
                itemsList: [
                    {
                        ...firstItem,
                        id: product.id,
                        inventoryItemName: product.name,
                        itemType: 'INVENTORY',
                        price: getEffectivePrice(product, selectedPartyId, applyCustomPricing) || 0,
                        unitSymbol: product.unitSymbol || '',
                        taxPlans: product.taxPlans || [],
                        hsnCode: product.hsnCode || '',
                    },
                    ...existingItems.slice(1),
                ],
            };

            await apiGenericUpdate(entityId, updated, entry.id);
            showToast("Entry updated!", TOAST_SUCCESS);
            onSaved && onSaved();
            onClose();
        } catch (error) {
            showToast("Error updating entry: " + (error.response?.data || error.message), TOAST_FAILURE);
        } finally {
            setIsSaving(false);
        }
    }, [row, entityId, partyField, applyCustomPricing, onClose, onSaved]);

    if (!row) return null;

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton className="bg-primary bg-opacity-10 py-3">
                <Modal.Title className="text-primary fs-5">
                    <i className="bi bi-pencil-square me-2"></i>
                    Edit {partyLabel} &amp; Product
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={formik.handleSubmit}>
                <Modal.Body>
                    <div className="mb-3 text-muted small">
                        Vehicle <span className="fw-bold text-dark">{row.vehicleNumber || 'N/A'}</span>
                    </div>

                    <Form.Group className="mb-4">
                        <AGCDynamicSelect
                            entityId={partyEntityId}
                            formik={formik}
                            name={partyLabel}
                            id="party"
                            value={formik.values.party}
                            toolTip={`Search and select ${partyLabel.toLowerCase()}`}
                            required={true}
                            searchField="name"
                        />
                        {formik.touched.party && formik.errors.party && (
                            <div className="text-danger small mt-1">{formik.errors.party}</div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-2">
                        {loadingItems ? (
                            <>
                                <Form.Label className="required">Product</Form.Label>
                                <div className="text-muted small">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Loading products...
                                </div>
                            </>
                        ) : (
                            <AGCStaticSelect
                                data={productOptions}
                                formik={formik}
                                name="Product"
                                id="product"
                                value={formik.values.product}
                                toolTip="Search and select product"
                                required={true}
                            />
                        )}
                        {selectedProduct && (
                            <div className="text-success small">
                                Rate: {formatCurrency(getEffectivePrice(selectedProduct, partyId, applyCustomPricing))}
                            </div>
                        )}
                        {formik.touched.product && formik.errors.product && (
                            <div className="text-danger small mt-1">{formik.errors.product}</div>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={isSaving}>Cancel</Button>
                    <Button type="submit" variant="primary" disabled={isSaving || loadingItems}>
                        {isSaving && <Spinner animation="border" size="sm" className="me-2" />}
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
